import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { dateFmt } from "@/lib/format";
import { PageLoader, EmptyState, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LifeBuoy, Send, Loader2, MessageSquareReply } from "lucide-react";

const CATEGORIES = [
  { value: "Sales discrepancy", label: "Sales discrepancy" },
  { value: "Payment / redemption", label: "Payment / redemption" },
  { value: "Reprint order", label: "Reprint order" },
  { value: "General concern", label: "General concern" },
];

export default function Support() {
  const qc = useQueryClient();
  const { data: tickets, isLoading } = useQuery({ queryKey: ["author-tickets"], queryFn: async () => (await api.get("/author/tickets")).data });

  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("Sales discrepancy");
  const [message, setMessage] = useState("");

  const submit = useMutation({
    mutationFn: async () => (await api.post("/author/tickets", { subject, category, message })).data,
    onSuccess: () => {
      toast.success("Your concern has been raised");
      setSubject(""); setMessage(""); setCategory("Sales discrepancy");
      qc.invalidateQueries({ queryKey: ["author-tickets"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const valid = subject.trim() && message.trim();

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Support</p>
        <p className="text-sm text-muted-foreground mt-1">Raise a discrepancy or share any concern with the Manda Publishers team.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:p-8 h-fit">
          <h3 className="font-head text-lg font-medium">Raise a concern</h3>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input data-testid="ticket-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary" />
            </div>
            <div className="space-y-2">
              <Label>Details</Label>
              <Textarea data-testid="ticket-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe the issue or concern in detail..." className="min-h-[120px]" />
            </div>
            <Button data-testid="submit-ticket" onClick={() => submit.mutate()} disabled={!valid || submit.isPending} className="w-full gap-2">
              {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit
            </Button>
          </div>
        </Card>

        <div className="lg:col-span-2">
          {isLoading ? (
            <PageLoader />
          ) : tickets.length === 0 ? (
            <EmptyState icon={LifeBuoy} title="No tickets yet" desc="Concerns you raise will be tracked here along with responses." />
          ) : (
            <div className="space-y-4">
              {tickets.map((t) => (
                <Card key={t.id} data-testid={`my-ticket-${t.id}`} className="p-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    {t.ticket_no && <Badge variant="outline" className="font-mono text-xs" data-testid={`my-ticket-no-${t.id}`}>{t.ticket_no}</Badge>}
                    <p className="font-head font-semibold">{t.subject}</p>
                    <Badge variant="secondary" className="capitalize font-normal">{t.category}</Badge>
                    <StatusBadge status={t.status} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{dateFmt(t.created_at)}</p>
                  <p className="text-sm mt-3 text-foreground/90 leading-relaxed whitespace-pre-wrap">{t.message}</p>
                  {t.admin_response && (
                    <div className="mt-4 rounded-lg bg-secondary p-4">
                      <p className="text-xs font-medium flex items-center gap-1.5 text-secondary-foreground"><MessageSquareReply className="h-3.5 w-3.5" /> Manda Publishers replied</p>
                      <p className="text-sm mt-1.5 text-secondary-foreground/90 whitespace-pre-wrap">{t.admin_response}</p>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
